import { useState } from 'react';
import {
  Tag,
  Dropdown,
  App as AntdApp,
  Button,
  Table,
  TablePaginationConfig,
  Modal,
  Skeleton,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { MoreVertical } from 'lucide-react';
import { useApprovedMeetingDetailsById, useMeetingByStatus } from '../../hooks/useMeetings';
import { MeetingByStatusResponse } from '../../types/meeting';
import UserManagementSkeleton from '../Skeleton/UserManagement';
import { sendReminder } from '../../service/meeting';
import EditApprovedMeetingModal from '../Forms/EditApprovedMeetingForm';
import { useAppStore } from '../../store/appStore';

const ApprovedMeeting = () => {
  const { message } = AntdApp.useApp();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedLinks, setSelectedLinks] = useState<{ hostLink?: string; attendeeLink?: string }>(
    {}
  );
  const [selectedMeetingId, setSelectedMeetingId] = useState<number>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const profile = useAppStore((state) => state.profile);
  const userId = profile.user.id;
    const isMarketingManager = profile?.user.role.roleName === 'Marketing Manager';



  // Fetch meetings with server-side pagination
  const {
    data: meetingList,
    isLoading,
    error,
  } = useMeetingByStatus('APPROVED', page, limit, userId);
  const data = meetingList?.data || [];

  const { data: approvedMeetingData, isLoading: isApprovedMeetingDataLoaded } =
    useApprovedMeetingDetailsById(selectedMeetingId as number);

  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title' },
    { title: 'Organization', dataIndex: 'organization', key: 'organization' },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
      sorter: (a: MeetingByStatusResponse, b: MeetingByStatusResponse) =>
        dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      render: (time: string) => dayjs(time, 'HH:mm:ss').format('hh:mm A'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color="cyan">{status}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right' as const,
      render: (_: unknown, record: MeetingByStatusResponse) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'join',
                label: 'Join Meeting',
              

                onClick: () => {
                  // open modal and show meeting links
                  setSelectedLinks({
                    hostLink: record.links?.hostLink,
                    attendeeLink: record.links?.attendeeLink,
                  });
                  setIsModalVisible(true);
                },
              },
              {
                key: 'notify',
                label: 'Notify',
                disabled: isMarketingManager, // ✅ DISABLED FOR MARKETING MANAGER
                onClick: async () => {
                  try {
                    if (record.id) {
                      await sendReminder(record.id);
                      message.success('Reminder sent successfully');
                    } else {
                      message.warning('Meeting ID not available');
                    }
                  } catch (error) {
                    console.error('Failed to send reminder:', error);
                    message.error('Failed to send reminder');
                  }
                },
              },
              {
                key: 'edit',
                label: 'Edit',
                onClick: () => {
                  editApprovedMeetingHandler(record.id);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreVertical size={16} />} />
        </Dropdown>
      ),
    },
  ];

  const editApprovedMeetingHandler = (id: number) => {
    setSelectedMeetingId(id);
    setIsModalOpen(true);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) setPage(pagination.current);
    if (pagination.pageSize) setLimit(pagination.pageSize);
  };

  if (isLoading) return <UserManagementSkeleton />;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      <Table
        dataSource={data?.filter((item:any) => item.status && item.status !== "COMPLETED")}
        columns={columns}
        rowKey="id"
        pagination={{
          current: meetingList?.pagination.page || page,
          pageSize: meetingList?.pagination.limit || limit,
          total: meetingList?.pagination.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} meetings`,
        }}
        onChange={handleTableChange}
        className="overflow-x-auto"
      />

      {/* Modal for Join Meeting Links */}
      <Modal
        title="Meeting Links"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <div className="space-y-3">
          <div>
            <strong>Host Link: </strong>
            {selectedLinks.hostLink ? (
              <span>
                <a
                  href={selectedLinks.hostLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {selectedLinks.hostLink}
                </a>
              </span>
            ) : (
              <div>No host link available</div>
            )}
          </div>
          <div>
            <strong>Attendee Link: </strong>
            {selectedLinks.attendeeLink ? (
              <span>
                <a
                  href={selectedLinks.attendeeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {selectedLinks.attendeeLink}
                </a>
              </span>
            ) : (
              <div>No attendee link available</div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        title="Details for Status: Requested"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
      >
        {!isApprovedMeetingDataLoaded ? (
          approvedMeetingData ? (
            <EditApprovedMeetingModal
              closeStatusModal={setIsModalOpen}
              meetingData={approvedMeetingData}
              onUpdate={async (values, id) => {
                console.log('Updated approved meeting:', values, id);
                // Optionally refetch meetings data here
              }}
            />
          ) : (
            <Typography.Text>No approved meeting data available</Typography.Text>
          )
        ) : (
          <Skeleton active />
        )}
      </Modal>
    </>
  );
};

export default ApprovedMeeting;
